import type {
  Language,
  ReviewCategory,
  RewriteExecuteResponse,
  RewriteMode,
  RewritePreviewResponse,
  ScanResponse,
  ScanResultItem,
} from "../types";
import { notifySessionUnauthorized } from "../authUnauthorized";
import { pickScanFullRewriteText } from "../utils/scanFullRewrite";
import { beginApiLoading, endApiLoading } from "../apiLoading";

const API_BASE_URL = (import.meta as ImportMeta & {
  env: Record<string, string | undefined>;
}).env.VITE_COMPLIANCE_API ?? "/api";

type TrackedFetchOptions = {
  /** 不触发全屏 Loading（页面内已有 Spin/Button loading 时用） */
  silent?: boolean;
};

function requestUrlForAuthCheck(input: RequestInfo | URL): string {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.href;
  }
  return input.url;
}

/** 登录/注册等接口也会返回 401，不应触发「会话失效」全局弹窗 */
function isAuthNegotiationUrl(url: string): boolean {
  const u = url.toLowerCase();
  return (
    u.includes("/auth/login") ||
    u.includes("/auth/register") ||
    u.includes("/auth/send-code")
  );
}

async function trackedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options?: TrackedFetchOptions,
): Promise<Response> {
  const silent = options?.silent === true;
  if (!silent) {
    beginApiLoading();
  }
  try {
    const response = await globalThis.fetch(input, init);
    if (
      response.status === 401 &&
      typeof window !== "undefined" &&
      !isAuthNegotiationUrl(requestUrlForAuthCheck(input))
    ) {
      notifySessionUnauthorized();
    }
    return response;
  } finally {
    if (!silent) {
      endApiLoading();
    }
  }
}

const highRiskRules = [
  /treat/i,
  /cure/i,
  /治疗/u,
  /根治/u,
  /药效/u,
  /diagnose/i,
];
const mediumRiskRules = [
  /anti-?inflammatory/i,
  /消炎/u,
  /improve anxiety/i,
  /改善焦虑/u,
];

/** 与后端 `compliance_engine._FALLBACK_SUMMARY` 保持一致，用于按界面语言回显摘要 */
export const SCAN_FALLBACK_SUMMARY: Record<Language, string> = {
  zh: "检测到疑似疾病治疗或药品化宣称，建议改为结构/功能描述并增加合规免责声明。",
  en: "Potential disease-treatment or drug-like claims detected. Reframe into structure/function benefits with compliant disclaimers.",
};

export const SCAN_NO_RISK_SUMMARY: Record<Language, string> = {
  zh: "未发现明显高风险宣称，可继续人工复核并补充免责声明。",
  en: "No obvious high-risk claims found. Please run manual review with proper disclaimers.",
};

const FLAG_REASON_ZH = {
  high: "出现疾病治疗/药品功效暗示，易触发 FDA 对未批准药品宣称的关注。",
  med: "表达接近疾病改善承诺，建议进一步弱化并补充适用边界。",
} as const;
const FLAG_REASON_EN = {
  high: "Contains disease-treatment or drug-like claims that may trigger FDA scrutiny for unapproved drug claims.",
  med: "The statement is close to disease-improvement promise and should be softened.",
} as const;

/** 扫描结果存的是某一语言的固定句式时，按当前界面语言切换（如切换 EN/中文后导出 PDF） */
function normSummary(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

export function scanSummaryForLang(
  summary: string | undefined,
  lang: Language,
): string {
  const raw = (summary ?? "").trim();
  const zh = SCAN_FALLBACK_SUMMARY.zh;
  const en = SCAN_FALLBACK_SUMMARY.en;
  const zhNoRisk = SCAN_NO_RISK_SUMMARY.zh;
  const enNoRisk = SCAN_NO_RISK_SUMMARY.en;
  const n = normSummary(raw);
  if (n === normSummary(zh) || n === normSummary(en)) {
    return SCAN_FALLBACK_SUMMARY[lang];
  }
  if (n === normSummary(zhNoRisk) || n === normSummary(enNoRisk)) {
    return SCAN_NO_RISK_SUMMARY[lang];
  }
  return raw || SCAN_FALLBACK_SUMMARY[lang];
}

function localizeFlagReason(reason: string, lang: Language): string {
  if (lang === "zh") {
    if (reason === FLAG_REASON_EN.high) return FLAG_REASON_ZH.high;
    if (reason === FLAG_REASON_EN.med) return FLAG_REASON_ZH.med;
    return reason;
  }
  if (reason === FLAG_REASON_ZH.high) return FLAG_REASON_EN.high;
  if (reason === FLAG_REASON_ZH.med) return FLAG_REASON_EN.med;
  return reason;
}

export function localizeScanForDisplay(
  result: ScanResponse,
  lang: Language,
): ScanResponse {
  return {
    ...result,
    summary: scanSummaryForLang(result.summary, lang),
    flaggedItems: result.flaggedItems.map((item) => ({
      ...item,
      reason: localizeFlagReason(item.reason, lang),
    })),
  };
}

function rewriteClaimEn(text: string): string {
  let rewritten = text;
  const replacements: Array<[string, string]> = [
    ["treat", "support"],
    ["cure", "help manage"],
    ["diagnose", "assess wellness status (non-diagnostic)"],
    ["chronic insomnia", "sleep quality concerns"],
    ["anxiety disorder", "daily stress and mood balance"],
    ["100% success rate", "results may vary by individual"],
    ["no side effects", "consult a professional before use"],
    ["replacement for prescription", "not a replacement for prescribed treatment"],
  ];
  replacements.forEach(([from, to]) => {
    const reg = new RegExp(from, "gi");
    rewritten = rewritten.replace(reg, to);
  });
  return rewritten;
}

/** 整段一键改写：与离线扫描及后端 `compliance_engine.full_document_rewrite` 规则一致。 */
export function fullDocumentRewriteFromClaims(
  content: string,
  lang: Language,
): string {
  const trimmed = content.trim();
  if (!trimmed) return "";
  return lang === "zh" ? rewriteClaimZh(trimmed) : rewriteClaimEn(trimmed);
}

function rewriteClaimZh(text: string): string {
  let rewritten = text;
  const replacements: Array<[string, string]> = [
    ["治疗", "支持改善"],
    ["治愈", "帮助调理"],
    ["诊断", "用于健康状态参考（非诊断）"],
    ["慢性失眠", "睡眠状态问题"],
    ["焦虑症", "日常情绪与压力管理"],
    ["100%成功率", "效果因人而异"],
    ["无任何副作用", "使用前请咨询专业人士"],
    ["替代处方", "不替代医生处方建议"],
  ];
  replacements.forEach(([from, to]) => {
    const reg = new RegExp(from, "g");
    rewritten = rewritten.replace(reg, to);
  });
  return rewritten;
}

function buildTargetedSuggestion(line: string, lang: Language): string {
  const lower = line.toLowerCase();
  if (lang === "zh") {
    if (lower.includes("100%") || line.includes("无任何副作用")) {
      return "删除“100%/无副作用”绝对化表述，改为“效果因人而异，使用前请咨询专业人士”。";
    }
    if (line.includes("治疗") || line.includes("治愈") || line.includes("诊断")) {
      return `改写示例：${rewriteClaimZh(line)}`;
    }
    return `建议改为支持性表达，示例：${rewriteClaimZh(line)}`;
  }
  if (lower.includes("100%") || lower.includes("no side effects")) {
    return "Remove absolute claims and use: 'results may vary' + 'consult a professional before use'.";
  }
  if (lower.includes("treat") || lower.includes("cure") || lower.includes("diagnose")) {
    return `Compliant rewrite example: ${rewriteClaimEn(line)}`;
  }
  return `Use softer support wording. Example rewrite: ${rewriteClaimEn(line)}`;
}

export interface ChatContextMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AuthLoginPayload {
  email: string;
  password: string;
}

export interface AuthRegisterPayload {
  email: string;
  code: string;
  password: string;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

export interface SendCodeResponse {
  ok: boolean;
  cooldown_seconds?: number;
  debug_code?: string;
}

export interface ConversationSummary {
  id: string;
  title: string;
  updated_at?: string;
}

export interface ConversationMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  created_at?: string;
}

export interface ConversationMessagesPage {
  items: ConversationMessage[];
  has_more: boolean;
  next_offset: number;
}

export interface BillingSummary {
  plan: {
    id: string;
    name: string;
    code: string;
    is_unlimited: boolean;
    scan_quota: number | null;
  } | null;
  renew_at: string | null;
  credit_balance: number;
  rewrite_balance?: number;
  month_used_scans: number;
  month_scan_quota: number | null;
  is_unlimited: boolean;
}

export interface ComplianceCreditsSnapshot {
  credit_balance: number;
  is_unlimited: boolean;
}

export interface ComplianceAccessSnapshot {
  can_use_studio: boolean;
  can_use_rewrite: boolean;
}

export interface CurrentUser {
  id: string;
  email: string;
  role?: string;
  language: "en" | "zh";
  theme_pref: "light" | "dark" | "system";
  subscription_id: string | null;
  credit_balance: number;
  rewrite_balance?: number;
}

export interface CreatedConversation {
  id: string;
  title: string;
  updated_at?: string;
}

export interface BillingHistoryItem {
  id: string;
  order_no: string;
  amount_usd_cents: number;
  status: string;
  paid_at: string | null;
  created_at: string;
}

export interface PlanListItem {
  id: string;
  code: string;
  name: string;
  billing_cycle: string;
  price_usd_cents: number;
  scan_quota: number | null;
  is_unlimited: boolean;
}

export interface DashboardOverview {
  scans_today: number;
  high_risk_blocks: number;
  avg_score: number;
  month_used_scans?: number;
  month_scan_quota?: number | null;
  api_usage_ratio?: number | null;
  system_alert?: {
    title: string;
    message: string;
    level?: "info" | "warning" | "error" | string;
  };
  recent_scans: Array<{
    id: string;
    time: string;
    product: string;
    risk: "high" | "medium" | "low" | string;
    status: string;
  }>;
}

export interface FeedbackItem {
  id: string;
  user_id: string;
  contact: string;
  question: string;
  status: "open" | "reviewing" | "resolved" | string;
  created_at: string;
  updated_at?: string;
}

export interface ReviewHistoryItem {
  id: string;
  created_at: string;
  input_text: string;
  score: number;
  summary: string;
  flagged_items: Array<{
    phrase: string;
    risk: "high" | "medium" | "low" | string;
    reason: string;
    suggestion: string;
  }>;
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "ApiError";
  }
}

async function readErrorDetail(response: Response, fallback: string): Promise<string> {
  try {
    const data = (await response.json()) as { detail?: string };
    if (data.detail) {
      return data.detail;
    }
  } catch {
    // ignore parse errors and use fallback message
  }
  return fallback;
}

function ensureApiBaseUrl() {
  if (!API_BASE_URL) {
    throw new Error("VITE_COMPLIANCE_API is not configured");
  }
  return API_BASE_URL;
}

function getAuthHeaders() {
  const token = localStorage.getItem("xtool_token");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

const responseCache = new Map<string, CacheEntry<unknown>>();
const inflightCache = new Map<string, Promise<unknown>>();
const DEFAULT_CACHE_TTL_MS = 30000;

async function fetchWithShortCache<T>(
  key: string,
  loader: () => Promise<T>,
  ttlMs: number = DEFAULT_CACHE_TTL_MS,
): Promise<T> {
  const now = Date.now();
  const cached = responseCache.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.value as T;
  }
  const inflight = inflightCache.get(key);
  if (inflight) {
    return (await inflight) as T;
  }
  const nextPromise = loader()
    .then((value) => {
      responseCache.set(key, {
        value,
        expiresAt: Date.now() + ttlMs,
      });
      return value;
    })
    .finally(() => {
      inflightCache.delete(key);
    });
  inflightCache.set(key, nextPromise as Promise<unknown>);
  return nextPromise;
}

export async function fetchFdaHealthcheck(): Promise<boolean> {
  if (!API_BASE_URL) {
    return false;
  }
  try {
    const response = await trackedFetch(`${API_BASE_URL}/rules/meta`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return false;
    }
    const data = (await response.json()) as { fda_endpoint_status?: string };
    const status = (data.fda_endpoint_status ?? "").toLowerCase();
    return status !== "offline";
  } catch {
    return false;
  }
}

export type ScanProgressPayload = {
  index: number;
  label: string;
  total: number;
  /** 0–100，供全屏进度条使用 */
  percent: number;
  /** 全部阶段已完成，进入收尾展示 */
  isComplete?: boolean;
};

export type ScanClaimsOptions = {
  /** 写入 review_tasks.product_display_name；扫描记录列表「产品名称」 */
  productDisplayName?: string;
  /** 关联系列（review_series.id）；主合规服务若支持会写入 review_tasks.series_id / version_no */
  seriesId?: string;
  /**
   * 默认 auto：服务端先做规则预检，再调 LLM 深扫并合并；无 LLM 或 LLM 失败则仅规则结果。
   * rules=仅规则；llm=规则+LLM，LLM 未配置或失败时 503。
   */
  scanMode?: "auto" | "rules" | "llm";
  /**
   * 扫描阶段提示：请求进行中按「实际等待时长」推进进度与阶段；返回后快速收尾再进入完成态。
   * 若后端以后提供 SSE/分阶段 API，可再替换为真实进度。
   */
  onProgress?: (p: ScanProgressPayload) => void;
};

/** 请求进行中进度条最大百分比（留余量给完成态） */
const SCAN_FETCH_PERCENT_CAP = 86;
/** 约每毫秒贡献的进度（接口慢时条仍缓慢增长，避免长时间卡死） */
const SCAN_FETCH_PERCENT_PER_MS = 0.012;
/** 轮询 UI 刷新间隔（仅影响动画顺滑度，不代表固定阶段秒数） */
const SCAN_FETCH_TICK_MS = 250;
/** 阶段索引：约每多少毫秒「进入下一文案」（与接口总耗时同步变长） */
const SCAN_STEP_ELAPSED_MS = 700;
const SCAN_DONE_HOLD_MS = 450;
/** 接口返回后补全步骤间隔 */
const SCAN_TAIL_STEP_MS = 100;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function scanProgressMessages(lang: Language): string[] {
  return lang === "zh"
    ? [
        "正在解析宣称文本与结构…",
        "大模型正在分析合规风险与监管关注点…",
        "正在匹配 FDA（openFDA）与 FTC（RSS）公开数据证据提示…",
        "正在生成合规分、摘要与改写建议…",
      ]
    : [
        "Parsing and structuring your claims…",
        "Model is analyzing compliance risk and regulatory focus…",
        "Matching FDA (openFDA) and FTC (RSS) public evidence signals…",
        "Scoring, summarizing, and preparing rewrite suggestions…",
      ];
}

/** 与进度条、全屏步骤列表同源，供 UI 渲染固定步骤文案 */
export function getScanProgressStepLabels(lang: Language): string[] {
  return [...scanProgressMessages(lang)];
}

type ScanProgressTiming = { idx: number; start: number };

function emitScanProgress(
  onProgress: NonNullable<ScanClaimsOptions["onProgress"]>,
  msgs: string[],
  idx: number,
  percent: number,
): void {
  const total = msgs.length;
  const safeIdx = Math.max(0, Math.min(total - 1, idx));
  onProgress({
    index: safeIdx,
    label: msgs[safeIdx]!,
    total,
    percent: Math.max(0, Math.min(100, Math.round(percent))),
  });
}

/** 根据已等待毫秒数计算进度条与阶段索引（最后一步留给「完成」） */
function fetchProgressFromElapsed(
  msgs: string[],
  elapsedMs: number,
): { idx: number; percent: number } {
  const total = msgs.length;
  const maxIdx = Math.max(0, total - 2);
  const idx = Math.min(maxIdx, Math.floor(elapsedMs / SCAN_STEP_ELAPSED_MS));
  const timeBased = 8 + elapsedMs * SCAN_FETCH_PERCENT_PER_MS;
  const stepBased = total > 0 ? ((idx + 1) / total) * SCAN_FETCH_PERCENT_CAP : 0;
  const percent = Math.min(SCAN_FETCH_PERCENT_CAP, Math.max(timeBased, stepBased));
  return { idx, percent };
}

async function finalizeScanProgressUI(
  lang: Language,
  onProgress: NonNullable<ScanClaimsOptions["onProgress"]>,
  state: ScanProgressTiming,
  msgs: string[],
): Promise<void> {
  const total = msgs.length;
  while (state.idx < total - 1) {
    state.idx += 1;
    const pct =
      SCAN_FETCH_PERCENT_CAP +
      ((state.idx + 1) / total) * (100 - SCAN_FETCH_PERCENT_CAP - 8);
    emitScanProgress(onProgress, msgs, state.idx, pct);
    await sleep(SCAN_TAIL_STEP_MS);
  }

  const completeLabel =
    lang === "zh"
      ? "审查完成，正在整理报告…"
      : "Review complete. Finalizing your report…";
  onProgress({
    index: total - 1,
    label: completeLabel,
    total,
    percent: 100,
    isComplete: true,
  });
  await sleep(SCAN_DONE_HOLD_MS);
}

export async function scanClaims(
  content: string,
  lang: Language,
  category: ReviewCategory = "supplement",
  options?: ScanClaimsOptions,
): Promise<ScanResponse> {
  const payload: Record<string, unknown> = { content, lang, category };
  const name = options?.productDisplayName?.trim();
  if (name) {
    payload.product_display_name = name;
  }
  const sid = options?.seriesId?.trim();
  if (sid) {
    payload.series_id = sid;
  }
  if (options?.scanMode) {
    payload.scan_mode = options.scanMode;
  }
  if (API_BASE_URL) {
    const msgs = scanProgressMessages(lang);
    const now = Date.now();
    const progressState: ScanProgressTiming = { idx: 0, start: now };
    let intervalId: number | null = null;
    const onProg = options?.onProgress;

    if (onProg) {
      emitScanProgress(onProg, msgs, 0, 6);
      intervalId = window.setInterval(() => {
        const elapsed = Date.now() - progressState.start;
        const { idx, percent } = fetchProgressFromElapsed(msgs, elapsed);
        progressState.idx = idx;
        emitScanProgress(onProg, msgs, idx, percent);
      }, SCAN_FETCH_TICK_MS);
    }

    const clearTick = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    try {
      const response = await trackedFetch(`${API_BASE_URL}/compliance/scan`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = (await response.json()) as ScanResponse;
        const fullDoc = pickScanFullRewriteText(data);
        const out = { ...data } as ScanResponse & Record<string, unknown>;
        delete out.rewrite_text;
        delete out.rewriteText;
        delete out.fullRewriteText;
        if (fullDoc) {
          out.full_rewrite_text = fullDoc;
        }
        clearTick();
        if (onProg) {
          await finalizeScanProgressUI(lang, onProg, progressState, msgs);
        }
        return out as ScanResponse;
      }
      clearTick();
      const detail = await readErrorDetail(response, "Compliance scan failed");
      throw new ApiError(response.status, detail);
    } catch (e) {
      clearTick();
      throw e;
    }
  }

  const lines = content
    .split(/[\n。.!?]/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (options?.onProgress) {
    const msgs = scanProgressMessages(lang);
    const t0 = Date.now();
    const state: ScanProgressTiming = { idx: 0, start: t0 };
    emitScanProgress(options.onProgress, msgs, 0, 8);
    const fakeDelay = Math.min(900, 120 + lines.length * 18);
    await sleep(fakeDelay);
    const elapsed = Date.now() - state.start;
    const mid = fetchProgressFromElapsed(msgs, elapsed);
    state.idx = mid.idx;
    emitScanProgress(options.onProgress, msgs, mid.idx, mid.percent);
    await finalizeScanProgressUI(lang, options.onProgress, state, msgs);
  }

  const flaggedItems: ScanResultItem[] = lines.reduce<ScanResultItem[]>((acc, line) => {
    if (highRiskRules.some((rule) => rule.test(line))) {
      acc.push({
        phrase: line,
        risk: "high",
        reason:
          lang === "zh"
            ? "出现疾病治疗/药品功效暗示，易触发 FDA 对未批准药品宣称的关注。"
            : "Contains disease-treatment or drug-like claims that may trigger FDA scrutiny for unapproved drug claims.",
        suggestion: buildTargetedSuggestion(line, lang),
      });
      return acc;
    }
    if (mediumRiskRules.some((rule) => rule.test(line))) {
      acc.push({
        phrase: line,
        risk: "medium",
        reason:
          lang === "zh"
            ? "表达接近疾病改善承诺，建议进一步弱化并补充适用边界。"
            : "The statement is close to disease-improvement promise and should be softened.",
        suggestion: buildTargetedSuggestion(line, lang),
      });
      return acc;
    }
    return acc;
  }, []);

  const score = Math.max(0, 100 - flaggedItems.length * 18);
  const full_rewrite_text = fullDocumentRewriteFromClaims(content, lang) || undefined;
  return {
    score,
    summary: flaggedItems.length ? SCAN_FALLBACK_SUMMARY[lang] : SCAN_NO_RISK_SUMMARY[lang],
    flaggedItems,
    ...(full_rewrite_text ? { full_rewrite_text } : {}),
  };
}

export async function sendRegisterCode(email: string): Promise<SendCodeResponse> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/auth/send-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, scene: "signup" }),
  });
  if (response.status === 429) {
    return (await response.json()) as SendCodeResponse;
  }
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Failed to send code"));
  }
  return (await response.json()) as SendCodeResponse;
}

export async function registerWithCode(payload: AuthRegisterPayload): Promise<AuthResponse> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Register failed"));
  }
  return (await response.json()) as AuthResponse;
}

export async function loginWithPassword(payload: AuthLoginPayload): Promise<AuthResponse> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Login failed"));
  }
  return (await response.json()) as AuthResponse;
}

export async function chatReply(
  content: string,
  lang: Language,
  messages: ChatContextMessage[] = [],
  conversationId?: string,
): Promise<{ answer: string; conversationId?: string }> {
  const payload = { content, lang, messages, conversation_id: conversationId };
  if (API_BASE_URL) {
    const response = await trackedFetch(`${API_BASE_URL}/chat/reply`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    if (response.ok) {
      const data = (await response.json()) as {
        answer?: string;
        conversation_id?: string;
      };
      if (data.answer) {
        return { answer: data.answer, conversationId: data.conversation_id };
      }
    }
    const detail = await readErrorDetail(response, "Chat reply failed");
    throw new ApiError(response.status, detail);
  }

  if (lang === "zh") {
    return {
      answer:
        "建议先明确目标功效，再避免“治疗/治愈/见效时限”这类高风险词。你也可以点“执行合规审查”获取结构化风险结论。",
    };
  }
  return {
    answer:
      "Start by clarifying the intended benefit, then avoid high-risk terms like treat/cure/time-bound efficacy. Run compliance review for structured risk output.",
  };
}

export async function chatReplyStream(
  content: string,
  lang: Language,
  messages: ChatContextMessage[] = [],
  conversationId: string | undefined,
  onDelta: (chunk: string) => void,
): Promise<{ answer: string; conversationId?: string }> {
  const baseUrl = ensureApiBaseUrl();
  const payload = { content, lang, messages, conversation_id: conversationId };
  const response = await trackedFetch(`${baseUrl}/chat/reply/stream`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok || !response.body) {
    const detail = await readErrorDetail(response, "Chat stream failed");
    throw new ApiError(response.status, detail);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let answer = "";
  let returnedConversationId = conversationId;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";

    for (const part of parts) {
      const lines = part.split("\n");
      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line.startsWith("data:")) {
          continue;
        }
        const data = line.slice(5).trim();
        if (!data || data === "[DONE]") {
          continue;
        }
        try {
          const parsed = JSON.parse(data) as {
            type?: string;
            delta?: string;
            answer?: string;
            conversation_id?: string;
          };
          if (parsed.type === "delta" && parsed.delta) {
            answer += parsed.delta;
            onDelta(parsed.delta);
          } else if (parsed.type === "done") {
            if (parsed.answer) {
              answer = parsed.answer;
            }
            if (parsed.conversation_id) {
              returnedConversationId = parsed.conversation_id;
            }
          }
        } catch {
          // ignore malformed SSE line
        }
      }
    }
  }

  return { answer, conversationId: returnedConversationId };
}

export async function fetchConversations(): Promise<ConversationSummary[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const response = await trackedFetch(`${API_BASE_URL}/chat/conversations`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    return [];
  }
  const data = (await response.json()) as { items?: ConversationSummary[] };
  return data.items ?? [];
}

export async function deleteConversation(conversationId: string): Promise<void> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/chat/conversations/${conversationId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Failed to delete conversation"));
  }
}

export async function fetchBillingSummary(): Promise<BillingSummary | null> {
  if (!API_BASE_URL) {
    return null;
  }
  return fetchWithShortCache("billing_summary", async () => {
    const response = await trackedFetch(`${API_BASE_URL}/billing/summary`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as BillingSummary;
  });
}

export async function fetchComplianceCredits(): Promise<ComplianceCreditsSnapshot | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const response = await trackedFetch(`${API_BASE_URL}/compliance/credits`, {
    headers: getAuthHeaders(),
    cache: "no-store",
  });
  if (!response.ok) {
    return null;
  }
  return (await response.json()) as ComplianceCreditsSnapshot;
}

export async function fetchComplianceAccess(): Promise<ComplianceAccessSnapshot | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const response = await trackedFetch(`${API_BASE_URL}/compliance/access`, {
    headers: getAuthHeaders(),
    cache: "no-store",
  });
  if (!response.ok) {
    return null;
  }
  return (await response.json()) as ComplianceAccessSnapshot;
}

export async function fetchPlans(): Promise<PlanListItem[]> {
  if (!API_BASE_URL) {
    return [];
  }
  return fetchWithShortCache("plans_list", async () => {
    const response = await trackedFetch(`${API_BASE_URL}/plans`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return [];
    }
    const data = (await response.json()) as { items?: PlanListItem[] };
    return data.items ?? [];
  });
}

export async function fetchCurrentUser(): Promise<CurrentUser | null> {
  if (!API_BASE_URL) {
    return null;
  }
  return fetchWithShortCache("current_user", async () => {
    const response = await trackedFetch(`${API_BASE_URL}/auth/me`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as CurrentUser;
  });
}

export async function fetchBillingHistory(): Promise<BillingHistoryItem[]> {
  if (!API_BASE_URL) {
    return [];
  }
  return fetchWithShortCache("billing_history", async () => {
    const response = await trackedFetch(`${API_BASE_URL}/billing/history`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return [];
    }
    const data = (await response.json()) as { items?: BillingHistoryItem[] };
    return data.items ?? [];
  });
}

export async function fetchDashboardOverview(): Promise<DashboardOverview | null> {
  if (!API_BASE_URL) {
    return null;
  }
  return fetchWithShortCache("dashboard_overview", async () => {
    const response = await trackedFetch(`${API_BASE_URL}/dashboard/overview`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return null;
    }
    return (await response.json()) as DashboardOverview;
  });
}

export async function submitFeedback(contact: string, question: string): Promise<void> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(
    `${baseUrl}/feedback`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ contact, question }),
    },
    { silent: true },
  );
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Failed to submit feedback"));
  }
  responseCache.delete("feedback_list");
}

export async function fetchFeedbackList(): Promise<FeedbackItem[]> {
  const baseUrl = ensureApiBaseUrl();
  return fetchWithShortCache("feedback_list", async () => {
    const response = await trackedFetch(
      `${baseUrl}/feedback`,
      { headers: getAuthHeaders() },
      { silent: true },
    );
    if (!response.ok) {
      return [];
    }
    const data = (await response.json()) as { items?: FeedbackItem[] };
    return data.items ?? [];
  });
}

export async function fetchReviewHistory(): Promise<ReviewHistoryItem[]> {
  if (!API_BASE_URL) {
    return [];
  }
  return fetchWithShortCache("review_history", async () => {
    const response = await trackedFetch(`${API_BASE_URL}/review/history`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      return [];
    }
    const data = (await response.json()) as { items?: ReviewHistoryItem[] };
    return data.items ?? [];
  });
}

export async function fetchConversationMessages(
  conversationId: string,
  options?: { limit?: number; offset?: number },
): Promise<ConversationMessagesPage> {
  if (!API_BASE_URL) {
    return { items: [], has_more: false, next_offset: 0 };
  }
  const limit = options?.limit ?? 10;
  const offset = options?.offset ?? 0;
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  const response = await trackedFetch(
    `${API_BASE_URL}/chat/conversations/${conversationId}/messages?${params.toString()}`,
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    return { items: [], has_more: false, next_offset: offset };
  }
  const data = (await response.json()) as ConversationMessagesPage;
  return {
    items: data.items ?? [],
    has_more: Boolean(data.has_more),
    next_offset:
      typeof data.next_offset === "number"
        ? data.next_offset
        : offset + (data.items?.length ?? 0),
  };
}

export async function createConversation(title: string): Promise<CreatedConversation> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/chat/conversations`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ title }),
  });
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Failed to create conversation"));
  }
  return (await response.json()) as CreatedConversation;
}

export async function appendConversationMessage(
  conversationId: string,
  role: "user" | "assistant" | "system",
  content: string,
): Promise<void> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/chat/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ role, content }),
  });
  if (!response.ok) {
    throw new Error(await readErrorDetail(response, "Failed to append conversation message"));
  }
}

export async function rewritePreview(
  content: string,
  lang: Language,
  category: ReviewCategory,
  mode: RewriteMode = "balanced",
): Promise<RewritePreviewResponse> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/rewrite/preview`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ content, lang, category, mode }),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Rewrite preview failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as RewritePreviewResponse;
}

export async function rewriteExecute(
  content: string,
  lang: Language,
  category: ReviewCategory,
  mode: RewriteMode = "balanced",
): Promise<RewriteExecuteResponse> {
  const baseUrl = ensureApiBaseUrl();
  const response = await trackedFetch(`${baseUrl}/rewrite/execute`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ content, lang, category, mode }),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Rewrite execute failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as RewriteExecuteResponse;
}

export type MonitorTopicDto = {
  id: string;
  label_zh: string;
  label_en: string;
  source: "builtin" | "user";
  keywords: string[];
};

export type MonitorTopicsResponseDto = {
  items: MonitorTopicDto[];
  created_slug?: string | null;
};

export type RegulatorySignalDto = {
  id: string;
  source: string;
  title: string;
  summary: string;
  link: string;
  published_at: string | null;
  topic_slugs: string[];
  signal_tier: string | null;
  tags: string[];
};

export type RegulatorySignalsListResponse = {
  items: RegulatorySignalDto[];
  total: number;
  page: number;
  page_size: number;
};

export type RegulatorySubscriptionDto = {
  enabled: boolean;
  frequency: "realtime" | "daily";
  topics: string[];
  notification_email: string;
};

export async function fetchRegulatoryMonitorTopics(): Promise<MonitorTopicsResponseDto | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const response = await trackedFetch(`${ensureApiBaseUrl()}/regulatory/monitor-topics`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Monitor topics request failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as MonitorTopicsResponseDto;
}

export async function createRegulatoryMonitorTopic(body: {
  label_zh: string;
  label_en: string;
  keywords: string[];
}): Promise<MonitorTopicsResponseDto | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const response = await trackedFetch(`${ensureApiBaseUrl()}/regulatory/monitor-topics`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Create monitor topic failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as MonitorTopicsResponseDto;
}

export async function deleteRegulatoryMonitorTopic(slug: string): Promise<boolean> {
  if (!API_BASE_URL) {
    return false;
  }
  const response = await trackedFetch(
    `${ensureApiBaseUrl()}/regulatory/monitor-topics/${encodeURIComponent(slug)}`,
    { method: "DELETE", headers: getAuthHeaders() },
  );
  return response.ok;
}

export async function fetchRegulatorySignals(opts: {
  page?: number;
  pageSize?: number;
  /** FDA | FTC | ALL */
  source?: string;
  /** Studio 监控主题 id，与后端关键词表对应；传齐四个等价于不按主题缩小 */
  monitorTopics?: string[];
  /** 在标题/摘要中额外 AND 匹配（产品名、成分等） */
  search?: string;
  lang: Language;
}): Promise<RegulatorySignalsListResponse | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const base = ensureApiBaseUrl();
  const q = new URLSearchParams({
    page: String(opts.page ?? 1),
    page_size: String(opts.pageSize ?? 10),
    source: opts.source ?? "ALL",
    lang: opts.lang === "zh" ? "zh" : "en",
  });
  for (const id of opts.monitorTopics ?? []) {
    const s = id.trim();
    if (s) {
      q.append("monitor_topics", s);
    }
  }
  const kw = (opts.search ?? "").trim();
  if (kw.length >= 2) {
    q.set("search", kw.slice(0, 200));
  }
  const response = await trackedFetch(`${base}/regulatory/signals?${q}`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Signals request failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as RegulatorySignalsListResponse;
}

export async function fetchRegulatorySubscription(): Promise<RegulatorySubscriptionDto | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const response = await trackedFetch(`${ensureApiBaseUrl()}/regulatory/subscription`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    if (response.status === 401) {
      return null;
    }
    const detail = await readErrorDetail(response, "Subscription request failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as RegulatorySubscriptionDto;
}

export interface StudioSeriesDto {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface StudioSeriesTaskDto {
  task_id: string;
  created_at: string;
  version_no: number | null;
  score: number | null;
  product_display_name: string | null;
}

export async function fetchStudioSeriesList(): Promise<StudioSeriesDto[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const response = await trackedFetch(`${ensureApiBaseUrl()}/studio/series`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to load series");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as StudioSeriesDto[];
}

export async function createStudioSeriesApi(title: string): Promise<StudioSeriesDto> {
  const base = ensureApiBaseUrl();
  const response = await trackedFetch(`${base}/studio/series`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ title: title.trim() }),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to create series");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as StudioSeriesDto;
}

export async function renameStudioSeriesApi(
  seriesId: string,
  title: string,
): Promise<StudioSeriesDto> {
  const response = await trackedFetch(
    `${ensureApiBaseUrl()}/studio/series/${encodeURIComponent(seriesId)}`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({ title: title.trim() }),
    },
  );
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to rename series");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as StudioSeriesDto;
}

export async function deleteStudioSeriesApi(seriesId: string): Promise<void> {
  const response = await trackedFetch(
    `${ensureApiBaseUrl()}/studio/series/${encodeURIComponent(seriesId)}`,
    {
      method: "DELETE",
      headers: getAuthHeaders(),
    },
  );
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to delete series");
    throw new ApiError(response.status, detail);
  }
}

export async function fetchStudioSeriesTasks(
  seriesId: string,
): Promise<StudioSeriesTaskDto[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const response = await trackedFetch(
    `${ensureApiBaseUrl()}/studio/series/${encodeURIComponent(seriesId)}/tasks`,
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to load series tasks");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as StudioSeriesTaskDto[];
}

export interface StudioScanListItemDto {
  task_id: string;
  created_at: string;
  score: number | null;
  product_display_name: string | null;
  product_vertical: string;
  series_id: string | null;
  version_no: number | null;
  scan_lang: "zh" | "en";
  input_preview: string;
}

export interface StudioScanDetailDto {
  task_id: string;
  created_at: string;
  input_text: string;
  product_display_name: string | null;
  product_vertical: string;
  series_id: string | null;
  version_no: number | null;
  scan_lang: "zh" | "en";
  result_json: Record<string, unknown> | null;
}

/** 当前账号在库中的审查任务列表（review_tasks） */
export async function fetchStudioScansList(params?: {
  limit?: number;
  offset?: number;
}): Promise<StudioScanListItemDto[]> {
  if (!API_BASE_URL) {
    throw new ApiError(0, "API not configured");
  }
  const sp = new URLSearchParams();
  if (params?.limit != null) sp.set("limit", String(params.limit));
  if (params?.offset != null) sp.set("offset", String(params.offset));
  const q = sp.toString();
  const url = `${ensureApiBaseUrl()}/studio/scans${q ? `?${q}` : ""}`;
  const response = await trackedFetch(url, { headers: getAuthHeaders() });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to load scans");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as StudioScanListItemDto[];
}

export async function fetchStudioScanDetail(
  taskId: string,
): Promise<StudioScanDetailDto> {
  if (!API_BASE_URL) {
    throw new ApiError(0, "API not configured");
  }
  const response = await trackedFetch(
    `${ensureApiBaseUrl()}/studio/scans/${encodeURIComponent(taskId)}`,
    { headers: getAuthHeaders() },
  );
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to load scan");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as StudioScanDetailDto;
}

/** 将审查任务关联系列或解除关联（写入 review_tasks.series_id / version_no） */
export async function patchReviewTaskSeries(
  taskId: string,
  seriesId: string | null,
): Promise<void> {
  if (!API_BASE_URL) {
    return;
  }
  const response = await trackedFetch(
    `${ensureApiBaseUrl()}/studio/tasks/${encodeURIComponent(taskId)}/series`,
    {
      method: "PATCH",
      headers: getAuthHeaders(),
      body: JSON.stringify({ series_id: seriesId }),
    },
  );
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Failed to attach series");
    throw new ApiError(response.status, detail);
  }
}

export async function putRegulatorySubscription(body: {
  enabled: boolean;
  frequency: "realtime" | "daily";
  topics: string[];
}): Promise<RegulatorySubscriptionDto> {
  const base = ensureApiBaseUrl();
  const response = await trackedFetch(`${base}/regulatory/subscription`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = await readErrorDetail(response, "Save subscription failed");
    throw new ApiError(response.status, detail);
  }
  return (await response.json()) as RegulatorySubscriptionDto;
}
